/**
 * TrialBanner.tsx — Dismissible top-of-page banner shown when a customer
 * is in trial with ≤ 7 days remaining.
 *
 * Dismissed state is persisted in localStorage for 24 hours.
 * Only rendered for the 'customer' role; hidden for engineers and admins.
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';

import CloseIcon from '@mui/icons-material/Close';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import TimerIcon from '@mui/icons-material/Timer';

import axios from 'axios';
import API_BASE from '../config/api';
import { useAuth } from '../context/AuthContext';

const DISMISS_KEY     = 'shield_trial_banner_dismissed_until';
const WARN_DAYS       = 7;   // show banner when ≤ this many days left

interface BillingStatus {
  status: string;
  days_until_trial_end: number | null;
  plan: string;
}

export default function TrialBanner() {
  const { user } = useAuth();
  const navigate  = useNavigate();
  const [status, setStatus]   = useState<BillingStatus | null>(null);
  const [visible, setVisible] = useState(false);

  // Only customers see the banner
  useEffect(() => {
    if (!user || user.role !== 'customer' || !user.customer_id) return;

    // Check if dismissed
    const dismissedUntil = localStorage.getItem(DISMISS_KEY);
    if (dismissedUntil && Date.now() < Number(dismissedUntil)) return;

    axios
      .get<BillingStatus>(`${API_BASE}/billing/${user.customer_id}/status`)
      .then(({ data }) => {
        if (
          data.status === 'trialing' &&
          data.days_until_trial_end !== null &&
          data.days_until_trial_end <= WARN_DAYS
        ) {
          setStatus(data);
          setVisible(true);
        }
      })
      .catch(() => {/* non-critical — silently ignore */});
  }, [user]);

  function dismiss() {
    setVisible(false);
    // Suppress for 24 hours
    localStorage.setItem(DISMISS_KEY, String(Date.now() + 24 * 60 * 60 * 1000));
  }

  if (!visible || !status) return null;

  const days = status.days_until_trial_end ?? 0;
  const urgentColor = days <= 1 ? '#ef233c' : days <= 3 ? '#f77f00' : '#f4d03f';
  const borderColor = days <= 1 ? 'rgba(239,35,60,0.4)' : days <= 3 ? 'rgba(247,127,0,0.4)' : 'rgba(244,208,63,0.4)';

  return (
    <Box
      sx={{
        px: { xs: 2, sm: 3 },
        py: 1,
        bgcolor: days <= 1 ? 'rgba(239,35,60,0.08)' : days <= 3 ? 'rgba(247,127,0,0.08)' : 'rgba(244,208,63,0.06)',
        borderBottom: `1px solid ${borderColor}`,
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        flexWrap: 'wrap',
      }}
    >
      <TimerIcon sx={{ color: urgentColor, fontSize: 20, flexShrink: 0 }} />

      <Typography variant="body2" sx={{ flex: 1, color: 'text.primary', fontWeight: 500 }}>
        <Box component="span" sx={{ color: urgentColor, fontWeight: 700 }}>
          {days === 0
            ? 'Your trial expires today'
            : days === 1
            ? 'Your trial expires tomorrow'
            : `${days} days left in your free trial`}
        </Box>
        {' — '}
        <Box component="span" sx={{ color: 'text.secondary' }}>
          add a payment method to keep your security monitoring running.
        </Box>
      </Typography>

      <Button
        size="small"
        variant="contained"
        startIcon={<CreditCardIcon />}
        onClick={() => navigate('/billing')}
        sx={{
          fontWeight: 700,
          bgcolor: urgentColor,
          '&:hover': { bgcolor: urgentColor, filter: 'brightness(1.1)' },
          flexShrink: 0,
        }}
      >
        Add Payment Method
      </Button>

      <IconButton size="small" onClick={dismiss} sx={{ color: 'text.disabled', flexShrink: 0 }}>
        <CloseIcon fontSize="small" />
      </IconButton>
    </Box>
  );
}
